import React, { useEffect, useState } from 'react';
import { subcontractAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { Loader2, ArrowUpRight, ArrowDownLeft, Package, Truck } from 'lucide-react';
import { toast } from 'sonner';

export function ChallanTrackingDialog({ open, onOpenChange, challan }) {
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);

  useEffect(() => {
    if (open && challan) {
      loadTracking();
    }
  }, [open, challan]);

  const loadTracking = async () => {
    if (!challan) return;
    setLoading(true);
    try {
      if (challan.order_type === 'inbound') {
        const res = await subcontractAPI.getInboundDispatchStatus(challan.id);
        setTrackingData({ type: 'inbound', ...res.data });
      } else {
        const res = await subcontractAPI.getOutboundReceiptStatus(challan.id);
        setTrackingData({ type: 'outbound', ...res.data });
      }
    } catch (err) {
      toast.error('Failed to load tracking data');
    } finally { setLoading(false); }
  };

  const isInbound = challan?.order_type === 'inbound';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isInbound ? <Truck className="w-5 h-5 text-teal-600" /> : <Package className="w-5 h-5 text-blue-600" />}
            {challan?.challan_number} — {isInbound ? 'Dispatch Tracking' : 'Receipt Tracking'}
          </DialogTitle>
          <DialogDescription>
            {isInbound
              ? `How items from ${challan?.supplier_name} have been dispatched back`
              : `How items sent to ${challan?.supplier_name} have been received back`}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : trackingData ? (
          <div className="space-y-4">
            {/* Per-item tracking */}
            {(trackingData.item_status || []).map((item, idx) => (
              <Card key={idx} data-testid={`tracking-item-${idx}`}>
                <CardHeader className="p-3 pb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-sm font-semibold">{item.item_name}</CardTitle>
                      <p className="text-[10px] text-muted-foreground font-mono">{item.item_unique_id || item.item_id}</p>
                    </div>
                    <div className="flex gap-2">
                      {isInbound ? (
                        <>
                          <Badge variant="outline" className="text-[10px]">Received: {item.received_qty}</Badge>
                          <Badge variant="outline" className="text-[10px] text-teal-600 border-teal-200">Dispatched: {item.dispatched_qty}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${item.pending_qty > 0 ? 'text-amber-600 border-amber-200' : 'text-emerald-600 border-emerald-200'}`}>
                            Pending: {item.pending_qty}
                          </Badge>
                        </>
                      ) : (
                        <>
                          <Badge variant="outline" className="text-[10px]">Sent: {item.sent_qty}</Badge>
                          <Badge variant="outline" className="text-[10px] text-teal-600 border-teal-200">Received: {item.received_qty}</Badge>
                          <Badge variant="outline" className={`text-[10px] ${item.pending_qty > 0 ? 'text-amber-600 border-amber-200' : 'text-emerald-600 border-emerald-200'}`}>
                            Pending: {item.pending_qty}
                          </Badge>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  {isInbound ? (
                    // Inbound: show dispatch challans
                    (item.dispatch_challans || []).length > 0 ? (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-1.5 font-medium text-muted-foreground">DC Number</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Date</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Qty</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.dispatch_challans.map((dc, dcIdx) => (
                            <tr key={dcIdx} className="border-b last:border-0">
                              <td className="p-1.5 font-medium">{dc.dc_number}</td>
                              <td className="p-1.5 text-center">{dc.date}</td>
                              <td className="p-1.5 text-center font-medium">{dc.quantity}</td>
                              <td className="p-1.5 text-center">
                                <Badge variant="outline" className={`text-[10px] ${dc.reconciliation_status === 'reconciled' ? 'text-emerald-600 border-emerald-200' : 'text-amber-600 border-amber-200'}`}>
                                  {dc.reconciliation_status || 'pending'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">No dispatches yet</p>
                    )
                  ) : (
                    // Outbound: show receipt entries
                    (item.receipt_entries || []).length > 0 ? (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Date</th>
                            <th className="text-left p-1.5 font-medium text-muted-foreground">Received By</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Received</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">OK</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Rejected</th>
                            <th className="text-center p-1.5 font-medium text-muted-foreground">Rework</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.receipt_entries.map((re, reIdx) => (
                            <tr key={reIdx} className="border-b last:border-0">
                              <td className="p-1.5">{re.date}</td>
                              <td className="p-1.5">{re.received_by}</td>
                              <td className="p-1.5 text-center font-medium">{re.received_qty}</td>
                              <td className="p-1.5 text-center text-emerald-600 font-medium">{re.ok_qty}</td>
                              <td className="p-1.5 text-center text-red-600 font-medium">{re.rejected_qty}</td>
                              <td className="p-1.5 text-center text-orange-600 font-medium">{re.rework_qty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <p className="text-xs text-muted-foreground text-center py-2">No receipts yet</p>
                    )
                  )}
                </CardContent>
              </Card>
            ))}

            {/* Work orders (for inbound) */}
            {isInbound && trackingData.work_orders && trackingData.work_orders.length > 0 && (
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm font-semibold">Related Work Orders</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-1.5 font-medium text-muted-foreground">WO #</th>
                        <th className="text-left p-1.5 font-medium text-muted-foreground">Item</th>
                        <th className="text-center p-1.5 font-medium text-muted-foreground">Qty</th>
                        <th className="text-center p-1.5 font-medium text-muted-foreground">Produced</th>
                        <th className="text-center p-1.5 font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackingData.work_orders.map((wo, idx) => (
                        <tr key={idx} className="border-b last:border-0">
                          <td className="p-1.5 font-medium">{wo.wo_number}</td>
                          <td className="p-1.5">{wo.item_name}</td>
                          <td className="p-1.5 text-center">{wo.quantity}</td>
                          <td className="p-1.5 text-center">{wo.produced_quantity || 0}</td>
                          <td className="p-1.5 text-center">
                            <Badge variant="outline" className="text-[10px]">{wo.status}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}

            {/* Scrap summary (for inbound) */}
            {isInbound && trackingData.scrap_summary && (
              <div className="flex gap-3 text-xs">
                <Badge variant="outline" className="text-[10px]">Scrap Produced: {trackingData.scrap_summary.total_produced}</Badge>
                <Badge variant="outline" className="text-[10px] text-teal-600 border-teal-200">Scrap Dispatched: {trackingData.scrap_summary.dispatched}</Badge>
                <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">Scrap Pending: {trackingData.scrap_summary.pending}</Badge>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-xs text-muted-foreground">No tracking data available</div>
        )}
      </DialogContent>
    </Dialog>
  );
}
